import React, { useCallback } from "react";
import { useController } from "react-hook-form";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";
import useAsyncOptionField from "components/Input/AsyncAutocomplete/useAsyncOptionField";
import { formBuilderParams } from "components/FormBuilder/FormBuilder";
import { queryMerchantOptions } from "../model";

const buildMerchantOption = (merchant) => {
  const merchantId = `${merchant?.merchantId ?? ""}`.trim();
  const name = `${merchant?.name ?? ""}`.trim();
  if (!merchantId) return null;

  return {
    key: merchantId,
    label: name ? `${merchantId} - ${name}` : merchantId,
    value: merchantId,
  };
};

const MerchantAsyncAutocomplete = ({
  formProps,
  defaultMerchant,
  disabled,
  label,
  placeholder,
  errorMessage,
}) => {
  const { clearErrors, control, setValue } = formProps;
  const { fieldState } = useController({
    name: "merchantId",
    control,
    defaultValue: "",
    rules: {
      required: errorMessage,
    },
  });

  const defaultMerchantId = `${defaultMerchant?.merchantId || ""}`.trim();
  const handleMerchantSelect = useCallback(
    (nextMerchant, { isUserChange }) => {
      setValue("merchantId", nextMerchant?.value || "", {
        shouldValidate: true,
        shouldDirty: isUserChange,
      });
      if (nextMerchant) clearErrors("merchantId");
    },
    [clearErrors, setValue],
  );
  const {
    fetchOptions,
    handleChange,
    options,
    selectedOption,
  } = useAsyncOptionField({
    queryOptions: queryMerchantOptions,
    mapOption: buildMerchantOption,
    getDefaultKeyword: useCallback(() => defaultMerchantId, [defaultMerchantId]),
    isDefaultOption: useCallback(
      (option) => `${option.value}` === defaultMerchantId,
      [defaultMerchantId],
    ),
    onSelect: handleMerchantSelect,
  });

  return (
    <div style={formBuilderParams.formItemStyle}>
      <AsyncAutocomplete
        label={label}
        mandatory
        fetchOptions={fetchOptions}
        preloadedOptions={options}
        value={selectedOption}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        error={!!fieldState.error}
        errorMessage={fieldState.error?.message}
      />
    </div>
  );
};

export default React.memo(MerchantAsyncAutocomplete);
